import React from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "../../../components/ui";
import {
  ENQUIRY_WELCOME_GREETING,
  ENQUIRY_WELCOME_QUESTION,
  ENQUIRY_WELCOME_QUESTION_TAIL,
  ENQUIRY_WELCOME_SUBTITLE,
  FORM_WELCOME_ACTION,
} from "../publicFormCopy";

/**
 * The first thing somebody sees when they open a practitioner's link.
 *
 * A screen of its own rather than a heading above the fields, and worth the
 * extra tap: the link arrives in a message with no explanation, so the first
 * job is to say whose form this is and what it is for. A stranger who lands
 * straight on nine empty inputs closes the tab.
 *
 * It asks for nothing, which is the point — there is no way to answer it
 * wrongly, and pressing the button is a small commitment that makes finishing
 * the form more likely than opening it cold.
 */

const PublicEnquiryWelcome: React.FC<{ onBegin: () => void }> = ({
  onBegin,
}) => (
  <div className="sm:text-center">
    {/*
      56px, semibold, line-height 120%, no letter-spacing — the design's own
      figures. Manrope comes from the app's default sans family, so it is
      inherited rather than named here; there is no `font-manrope` utility on
      purpose (see `tailwind.config.js`).

      "Hi," gets a hard break at every width, and the question under it gets
      one too — but only on a phone, where it is set as "how we may / help
      you?". From `sm:` up that break is dropped and the question runs on one
      line, which is what the two mockups show. Two rules, because they are two
      shapes; see `publicFormCopy`, where the words are split for it.

      The `short:` figures are the same screen on a handset that has no height
      to spare. This card is not allowed to scroll — see `fit` on the shell —
      so on a small phone something has to give, and a greeting two points
      smaller is a far better trade than a button cut off at the bottom edge.
    */}
    <h1 className="text-[48px] short:text-[44px] lg:text-[52px] font-semibold leading-[1.2] tracking-normal text-[#0A4A52]">
      {ENQUIRY_WELCOME_GREETING}
      <br />
      {ENQUIRY_WELCOME_QUESTION}
      {/* The space carries the wide screen: with the break hidden, the two
          halves have to close up into one sentence rather than run together
          into one word. */}
      <br className="sm:hidden" />{" "}
      {ENQUIRY_WELCOME_QUESTION_TAIL}
    </h1>

    {/* The practitioner speaking in the first person, which is whose page this
        is. Not "help <brand> understand": a brand name dropped into the middle
        of a sentence reads as a mail merge, and this is the one line on the
        screen that should sound like a person. */}
    <p className="mt-4 short:mt-3 sm:mt-5 text-[18px] short:text-[16px] sm:text-[20px] leading-7 text-brand-dark">
      {ENQUIRY_WELCOME_SUBTITLE}
    </p>

    <div className="mt-10 short:mt-7 sm:mt-12 flex justify-start sm:justify-center">
      <Button
        size="lg"
        onClick={onBegin}
        endIcon={<ArrowRight size={18} aria-hidden="true" />}
        className="min-w-[165px]"
      >
        {FORM_WELCOME_ACTION}
      </Button>
    </div>
  </div>
);

export default PublicEnquiryWelcome;
